/** leads/LeadPortal.tsx — 리드포탈 v4 "나의 법인DB 현황" (한화 법인DB 앱 레이아웃 차용, 사용자 확정 2026-09-05)
 *  화면: 홈(수락하기 | 관리 DB) · 나의 미팅 현황(n차 누적) · 법인 정보 확인 시트(LeadPortalInfoSheet).
 *  수락하기 탭 재정리(디자인 핸드오프 venture_deduction v2 화면 3a, 2026-09-10): 정원 배너는 가득 찼을 때만(수락 비활성),
 *  목록 헤더 2단(배정된 DB n건 | 관리 DB a/b · 잔여 r), 카드 = 법인명·수락 대기 배지·「배정 날짜」 메타·투자 요약·
 *  버튼 한 줄(수락하기 flex:1 / 받지 않기 92px → 확인 시트). 반복 안내문은 삭제, 탭 최초 진입 시 1회 안내만.
 *  v3 대비: 상담자료 설정·상담자료 준비 버튼 제거(사용자 지시) · 우측 보유 n건/n건 표기 · 탭/카드/시트 원본 레이아웃(폰트·정렬) 계승.
 *  용어: 수락(선점)/관리중/미팅결과/관리거절. FP·WM 등 내부용어는 호칭에만. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './leads.css';
import { portalHome, portalDetail, portalClaim, portalActivity, portalMeeting, portalAccept, portalDecline } from './api';
import LeadProposal from './LeadProposal';
import type { PortalLead, CartInfo, LeadActivity, LeadStatus } from './types';
import { C, tdef, cat, CATS, CAT_STYLE, MEET_TYPES, fmtKo, fmtDot, fmtDotYo, dday, ddayText, ddayStyle, expiryOf, newDeadlineOf, typeLine, guideText } from './portalFmt';
import { fmtAmount } from './calc';
import LeadPortalInfoSheet from './LeadPortalInfoSheet';
import { manwon } from './portalFmt';

/** 포털 상태 선택(카테고리 → 리드 상태). 미팅예정=assigned · 미팅진행중=in_progress · 미팅완료=hold · 계약완료=won (v4c 2026-09-05) */
const CAT_TO_STATUS: Record<string, LeadStatus> = { 미팅예정: 'assigned', 미팅진행중: 'in_progress', 미팅완료: 'hold', 계약완료: 'won' };

interface Props { token: string }
interface Meeting { round: number; text: string; manage: string; type: string; created_at: string }

function parseMeetings(acts: LeadActivity[]): Meeting[] {
  return acts.filter((a) => a.kind === 'meeting').map((a) => {
    try { const j = JSON.parse(a.content || '{}'); return { round: j.round || 1, text: j.text || '', manage: j.manage || '관리함', type: j.type || '', created_at: a.created_at }; }
    catch { return { round: 1, text: a.content || '', manage: '관리함', type: '', created_at: a.created_at }; }
  }).sort((a, b) => b.round - a.round);
}

/** 카드 하단 유형 한 줄 — 만료 유형은 D-day 칩 동반 */
/* ── 회사(사업자번호) 단위 묶음 — 사용자 지시 2026-09-18 「똑같은 업체가 2개씩 들어간 건 오류」 ──
 *  리드는 «사업자번호 × 납입연도»라 한 회사에 투자연도가 여럿이면 카드도 그만큼 생겼다.
 *  화면·정원은 «회사 1곳 = 1건»이어야 하므로 대표 1건만 남기고 나머지는 합산해 보여준다.
 *  (제안서는 서버에서 같은 법인의 전 연도를 합쳐 내려주므로 대표 1건으로 열어도 내용은 그대로다) */
const CO_STEP: Record<string, number> = { won: 5, hold: 4, in_progress: 3, assigned: 2 };
/** 묶음 키 — 사업자번호(숫자 10자리↑), 마스킹·미등록이면 법인명 */
function ckey(l: PortalLead): string {
  const d = String(l.biz_no || '').replace(/\D/g, '');
  return d.length >= 10 ? d : 'n:' + (l.corp_name || l.id);
}
/** 대표 리드 = 가장 진행된 상태 → 동률이면 가장 이른 납입연도 (미팅·이력이 붙은 건이 대표가 되도록) */
function betterRep(a: PortalLead, b: PortalLead): PortalLead {
  const sa = CO_STEP[a.status] || 0; const sb = CO_STEP[b.status] || 0;
  if (sa !== sb) return sa > sb ? a : b;
  const ya = Number(a.detail?.invest_year) || 9999; const yb = Number(b.detail?.invest_year) || 9999;
  if (ya !== yb) return ya < yb ? a : b;
  return String(a.id) <= String(b.id) ? a : b;   // 마지막은 id 순 — 새로고침해도 대표가 바뀌지 않게
}
interface Co { rep: PortalLead; all: PortalLead[] }
function groupCo(list: PortalLead[]): Co[] {
  const m = new Map<string, Co>();
  for (const l of list) {
    const k = ckey(l); const g = m.get(k);
    if (!g) m.set(k, { rep: l, all: [l] });
    else { g.all.push(l); g.rep = betterRep(g.rep, l); }
  }
  return Array.from(m.values());
}

function TypeLine({ l, co }: { l: PortalLead; co?: PortalLead[] }) {
  const t = tdef(l.lead_type);
  const d = t.cert ? dday(expiryOf(l)) : l.lead_type === 'venture_new' ? dday(newDeadlineOf(l)) : null; // 신규법인 = 벤처확인 기한 D-day
  // 같은 회사에 납입연도가 여럿이면 한 줄로 합쳐 쓴다 — 「경정청구 · 2023·2024년 9,000만 투자」
  const rows = (co || []).filter((x) => (x.lead_type || 'venture_refund') === 'venture_refund' && x.detail?.invest_year);
  const multi = l.lead_type === 'venture_refund' && rows.length > 1;
  const ys = multi ? Array.from(new Set(rows.map((x) => Number(x.detail!.invest_year)))).sort((a, b) => a - b) : [];
  const won = multi ? rows.reduce((a, x) => a + (Number(x.detail!.invest_amount) || 0), 0) : 0;
  return (
    <div className="lp4-tline">
      <span className="dot" style={{ background: t.fg }} />
      <span>{multi ? `${t.label} · ${ys.join('·')}년 ${fmtAmount(won)} 투자` : typeLine(l)}</span>
      {d != null && d <= 365 && <span className="lp4-dchip" style={{ color: ddayStyle(d).fg, background: ddayStyle(d).bg }}>{ddayText(d)}</span>}
    </div>
  );
}

export default function LeadPortal({ token }: Props) {
  const [fp, setFp] = useState<{ name: string; company: string; kind?: string } | null>(null);
  const [cart, setCart] = useState<CartInfo | null>(null);
  const [leads, setLeads] = useState<PortalLead[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState<'accept' | 'all'>('all');
  const tabPicked = useRef(false);   // 첫 로딩 때 한 번만 자동 선택 (이후엔 사용자가 고른 탭을 건드리지 않는다)
  const [catF, setCatF] = useState('전체');
  const [q, setQ] = useState('');
  const [toast, setToast] = useState('');
  const [info, setInfo] = useState<PortalLead | null>(null);        // 법인정보 시트
  const [meetLead, setMeetLead] = useState<PortalLead | null>(null); // 미팅 현황 화면
  const [meetings, setMeetings] = useState<Meeting[] | null>(null);
  const [regFor, setRegFor] = useState<PortalLead | null>(null);     // 결과 등록 모달
  const [propFor, setPropFor] = useState<PortalLead | null>(null);   // 경정청구 제안서 (A4 2쪽, 2026-09-10)
  const [openRound, setOpenRound] = useState(0);
  const [busy, setBusy] = useState(false);
  const say = (m: string) => { setToast(m); window.setTimeout(() => setToast(''), 2400); };
  // 제안서 직접 진입 — /lead-portal/:token?proposal=<leadId>[&print=1][&inc=a,b,c] (2026-09-19)
  //  print=1 은 서버 PDF 렌더(proposalPdf.js)가 여는 인쇄 모드: 상단 바 없이 A4 그대로. 목록은 불러오지 않는다.
  const [direct] = useState<{ id: string; print: boolean; inc: string } | null>(() => {
    try { const u = new URLSearchParams(window.location.search); const id = u.get('proposal'); return id ? { id, print: u.get('print') === '1', inc: u.get('inc') || '' } : null; } catch { return null; }
  });

  const reload = useCallback(async () => {
    try {
      const d = await portalHome(token);
      setFp(d.fp); setCart(d.cart); setLeads(d.leads);
    } catch (e) { say((e as Error).message); } finally { setLoaded(true); }
  }, [token]);
  useEffect(() => { if (!direct) reload(); }, [reload, direct]);

  // 097 (사용자 확정 2026-09-10): 관리자가 배정한 건은 「수락하기」에 먼저 들어가고(pending), 수락해야 「관리 DB」(mine)로 이동
  const isPending = (l: PortalLead) => l.mine && l.accepted === false && l.status !== 'lost' && l.status !== 'won';
  // 목록은 «회사 1곳 = 카드 1장» (2026-09-18). 대표 1건만 노출하고 같은 회사의 나머지 연도는 합산·일괄 처리한다.
  const mineCo = useMemo(() => groupCo(leads.filter((l) => l.mine && l.status !== 'lost' && l.accepted !== false)), [leads]);
  const pendingCo = useMemo(() => groupCo(leads.filter(isPending)), [leads]);
  const poolCo = useMemo(() => groupCo(leads.filter((l) => !l.mine && l.status === 'assigned')), [leads]);
  const mine = useMemo(() => mineCo.map((g) => g.rep), [mineCo]);
  const pending = useMemo(() => pendingCo.map((g) => g.rep), [pendingCo]);
  const pool = useMemo(() => poolCo.map((g) => g.rep), [poolCo]);
  /** 대표 리드 id → 그 회사의 리드 전부 (수락·받지않기를 회사 단위로 한 번에 처리하기 위해) */
  const coAll = useMemo(() => {
    const m = new Map<string, PortalLead[]>();
    [...mineCo, ...pendingCo, ...poolCo].forEach((g) => m.set(String(g.rep.id), g.all));
    return m;
  }, [mineCo, pendingCo, poolCo]);
  const allOf = (l: PortalLead) => coAll.get(String(l.id)) || [l];
  // 회사 카드에 쓰는 합계 — 그 회사 전 연도의 환급예상액·연도 목록 (제안서도 전부 합해 나온다)
  const coSum = useMemo(() => {
    const m = new Map<string, { count: number; sum: number; years: number[]; ready: boolean }>();
    mineCo.forEach((g) => {
      const rows = g.all.filter((l) => (l.lead_type || 'venture_refund') === 'venture_refund' && (l.estimated_refund || 0) > 0 && l.status !== 'lost');
      if (!rows.length) return;
      m.set(String(g.rep.id), {
        count: rows.length,
        // 카드마다 찍히던 만원 단위 값을 더한다 (원값 합계는 반올림 때문에 화면 숫자와 1만원 어긋날 수 있음)
        sum: rows.reduce((a, l) => a + Math.round((l.estimated_refund || 0) / 10000), 0),
        years: Array.from(new Set(rows.map((l) => Number(l.detail?.invest_year)).filter(Boolean))).sort((a, b) => a - b),
        ready: rows.some((l) => !!l.proposal_ready),
      });
    });
    return m;
  }, [mineCo]);
  const qq = q.trim();
  const byQ = (l: PortalLead) => qq.length < 2 || l.corp_name.includes(qq);
  const shown = mine.filter(byQ).filter((l) => catF === '전체' || cat(l.status) === catF);
  const poolShown = pool.filter(byQ);
  const pendingShown = pending.filter(byQ);
  const acceptCount = pending.length + pool.length;
  // 첫 진입 탭: 수락할 건이 있으면 「수락하기」, 없으면 「관리 DB」 (사용자 지시 2026-09-18).
  //  이후 배정이 들어와도 보고 있던 탭을 빼앗지 않도록 최초 1회만 판단한다.
  useEffect(() => {
    if (!loaded || tabPicked.current) return;
    tabPicked.current = true;
    setTab(acceptCount > 0 ? 'accept' : 'all');
  }, [loaded, acceptCount]);
  const catCnt = (c: string) => (c === '전체' ? mine.length : mine.filter((l) => cat(l.status) === c).length);
  const full = !!cart && cart.remaining <= 0;                        // 지점 공유 DB 선점 기준(수락 대기 포함 점유)
  const acceptFull = !!cart && cart.size > 0 && cart.used >= cart.size; // 배정 건 수락 기준(관리 DB가 실제로 가득) — 서버 accept 가드와 동일
  const acceptLeft = cart ? Math.max(0, cart.size - cart.used) : 0;
  const who = fp ? `${fp.name} ${fp.kind === 'wm' ? 'WM' : 'FP'}님` : '';

  const openMeet = async (l: PortalLead) => {
    setMeetLead(l); setMeetings(null);
    try { const d = await portalDetail(token, l.id); const ms = parseMeetings(d.activities); setMeetings(ms); setOpenRound(ms.length ? ms[0].round : 0); }
    catch (e) { say((e as Error).message); setMeetings([]); }
  };
  // 수락·받지않기는 «회사 단위» — 카드가 회사 1장이므로 그 회사의 납입연도 건을 한 번에 처리한다 (2026-09-18)
  const claim = async (l: PortalLead) => {
    if (busy) return; setBusy(true);
    try { for (const x of allOf(l)) await portalClaim(token, x.id); say('수락했습니다 — 관리 DB에서 확인하세요'); await reload(); }
    catch (e) { say((e as Error).message); await reload(); } finally { setBusy(false); }
  };
  /** 관리자 직배정 건 수락 → 관리 DB (097) */
  const accept = async (l: PortalLead) => {
    if (busy) return; setBusy(true);
    try { for (const x of allOf(l)) await portalAccept(token, x.id); say('수락했습니다 — 관리 DB로 이동했습니다'); await reload(); setTab('all'); }
    catch (e) { say((e as Error).message); await reload(); } finally { setBusy(false); }
  };
  /** 받지 않기 — 되돌릴 수 없으므로 확인 시트를 거친다 (핸드오프 3a) */
  const [declineFor, setDeclineFor] = useState<PortalLead | null>(null);
  const decline = async (l: PortalLead) => {
    if (busy) return; setBusy(true);
    try { for (const x of allOf(l)) await portalDecline(token, x.id); setDeclineFor(null); say('받지 않기로 했습니다'); await reload(); }
    catch (e) { say((e as Error).message); await reload(); } finally { setBusy(false); }
  };
  /** 인증 만료 DB 제안서 = 기존 만료 안내 DM(A4 1장 · FP 소속 테마 · 인쇄/PDF 툴바) — 새 탭으로, 막히면 같은 탭 (사용자 지시 2026-09-19 「원래 있던 양식 그대로」) */
  const openDm = (l: PortalLead) => {
    const url = '/api/lead-portal/' + token + '/leads/' + l.id + '/dm';
    const w = window.open(url, '_blank'); // noopener 를 주면 항상 null 이 돌아와 같은 탭으로도 한 번 더 열리므로 쓰지 않는다 (같은 출처 우리 페이지)
    if (!w) window.location.assign(url);
  };
  // 수락하기 탭 최초 진입 1회 안내 (카드마다 반복되던 안내문 대체)
  const [hintOff, setHintOff] = useState(() => { try { return localStorage.getItem('lp_accept_hint') === '1'; } catch { return false; } });
  const closeHint = () => { setHintOff(true); try { localStorage.setItem('lp_accept_hint', '1'); } catch { /* 무시 */ } };
  /** 미팅 현황 화면의 상태 선택 — 사용자 지시 2026-09-05 '업체 들어가서 상태값 선택할 수 있어야' */
  const setStatus = async (l: PortalLead, label: string) => {
    const to = CAT_TO_STATUS[label];
    if (!to || cat(l.status) === label || busy) return;
    if (to === 'won' && !window.confirm('계약 완료로 보고할까요?\n확정하면 지도사 업무 목록에 자동 등록됩니다.')) return;
    setBusy(true);
    try {
      await portalActivity(token, l.id, { kind: 'status', content: '상태 변경: ' + cat(l.status) + ' → ' + label, status_to: to });
      setMeetLead({ ...l, status: to });
      say(to === 'won' ? '축하합니다! 계약 완료로 등록됐습니다' : label + ' 상태로 변경했습니다');
      reload();
    } catch (e) { say((e as Error).message); } finally { setBusy(false); }
  };
  const StatusBadge = ({ l }: { l: PortalLead }) => { const cc = cat(l.status); const st = CAT_STYLE[cc]; return <span className="lp4-badge" style={{ color: st.fg, background: st.bg }}>{cc}</span>; };

  /* ───────── 화면: 제안서 (A4 2쪽) — 경정청구 · 인증 연장 ───────── */
  if (direct) {
    return <LeadProposal token={token} leadId={direct.id} corpName="" print={direct.print} incomesInit={direct.inc} onBack={() => { window.location.href = window.location.pathname; }} />;
  }
  if (propFor) {
    return <LeadProposal token={token} leadId={propFor.id} corpName={propFor.corp_name} onBack={() => setPropFor(null)} />;
  }

  /* ───────── 화면: 나의 미팅 현황 ───────── */
  if (meetLead) {
    const l = meetLead;
    const ms = meetings || [];
    return (
      <div className="lp4-shell">
        <header className="lp4-head">
          <button className="lp4-back" onClick={() => { setMeetLead(null); reload(); }} aria-label="뒤로">‹</button>
          <span className="lp4-title sub">나의 미팅 현황</span>
          {ms.length > 0 && <button className="lp4-pill act" onClick={() => setRegFor(l)}>✎ 결과 추가등록</button>}
        </header>
        <div className="lp4-body" style={{ paddingBottom: 120 }}>
          <div className="lp4-corp">{l.corp_name}</div>
          <div className="lp4-date">수락일 : {fmtKo(l.assigned_at)}</div>
          <TypeLine l={l} />
          <div className="lp4-hr" />
          <div className="lp4-stLab">진행 상태</div>
          <div className="lp4-stRow">
            {CATS.filter((c) => c !== '전체').map((c) => {
              const on = cat(l.status) === c; const st = CAT_STYLE[c];
              return <button key={c} className={'lp4-st' + (on ? ' on' : '')} style={on ? { background: st.bg, borderColor: st.bg, color: st.fg } : {}} disabled={busy} onClick={() => setStatus(l, c)}>{c}</button>;
            })}
          </div>
          <div className="lp4-hr" />
          {meetings == null ? (
            <div className="lp4-empty"><span>불러오는 중…</span></div>
          ) : ms.length === 0 ? (
            <div className="lp4-empty"><span className="dot" />법인 고객과 미팅은 어떠셨나요?<br />미팅 결과를 등록해주세요.</div>
          ) : ms.map((m) => (
            <div key={m.round} className="lp4-acc">
              <button className="lp4-accHead" onClick={() => setOpenRound(openRound === m.round ? 0 : m.round)} aria-expanded={openRound === m.round}>
                <b style={{ color: C.coral }}>{m.round}차</b>&nbsp;미팅 결과
                <span style={{ flex: 1 }} /><span style={{ color: C.sub, fontSize: 12.5 }}>{String(m.created_at).slice(0, 10)}</span>
                <span className="chev" style={{ transform: openRound === m.round ? 'rotate(90deg)' : 'none' }}>›</span>
              </button>
              {openRound === m.round && (
                <div className="lp4-accBody">
                  <div className="lp4-row"><span>내용</span><div style={{ whiteSpace: 'pre-wrap' }}>{m.text || '-'}</div></div>
                  <div className="lp4-row"><span>관리 여부</span><div>{m.manage}</div></div>
                  <div className="lp4-row"><span>상담 유형</span><div>{m.type || '-'}</div></div>
                </div>
              )}
            </div>
          ))}
          <div className="lp4-help">궁금한 점은 센터로 문의해 주세요 · <a href="tel:010-2369-0747">010-2369-0747</a></div>
        </div>
        {l.status !== 'won' && (
          <div className="lp4-sticky"><button className="lp4-cta flat" onClick={() => setRegFor(l)}>미팅결과 등록</button></div>
        )}
        {regFor && <MeetRegModal lead={regFor} token={token} onClose={() => setRegFor(null)}
          onSaved={(released) => { setRegFor(null); if (released) { say('관리거절 접수 — 다른 담당자께 재배정됩니다'); setMeetLead(null); reload(); } else { openMeet(l); reload(); } }} onToast={say} />}
        {toast && <div className="lp4-toast">{toast}</div>}
      </div>
    );
  }

  /* ───────── 홈 ───────── */
  return (
    <div className="lp4-shell">
      <header className="lp4-head">
        <span className="lp4-title">{who ? `${who}의 법인 DB` : '나의 법인DB 현황'}</span>
      </header>
      {who && fp && fp.company ? <div className="lp4-who">{fp.company}</div> : null}
      <div className="lp4-tabs">
        {/* 관리 DB 옆 보유/정원(장바구니) — 사용자 지시 2026-09-05 '10/10이 여기 적혀야 해'. 수락하기 탭은 항상 표시(비어 있으면 공란 안내) */}
        {([['accept', '수락하기', acceptCount ? String(acceptCount) : ''], ['all', '관리 DB', cart ? cart.used + '/' + cart.size : '']] as const).map(([k, label, n]) => (
          <button key={k} className={'lp4-tab' + (tab === k ? ' on' : '')} onClick={() => setTab(k)}>{label}{n ? <span className={'cnt' + (k === 'accept' ? ' hot' : '')}>{n}</span> : null}</button>
        ))}
      </div>
      <div className="lp4-searchWrap">
        <input className="lp4-search" placeholder="법인명을 2자 이상 입력해주세요." value={q} onChange={(e) => setQ(e.target.value)} aria-label="법인명 검색" />
        <svg className="lp4-searchIco" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="#8C8B98" strokeWidth="2" /><path d="M16.5 16.5L21 21" stroke="#8C8B98" strokeWidth="2" strokeLinecap="round" /></svg>
      </div>
      {tab === 'all' && (
        <div className="lp4-pills">
          {CATS.map((c) => (
            <button key={c} className={'lp4-cat' + (catF === c ? ' on' : '')} onClick={() => setCatF(c)}>{c} <em>{catCnt(c)}</em></button>
          ))}
        </div>
      )}
      <div className="lp4-body">
        {tab === 'all' && (<>
          {shown.map((l) => {
            // 환급 예상 블록 + 제안서 진입 (채택안 2a: 카드 전체가 탭 영역, 하단 버튼 2개는 전파 차단) — 금액이 없으면 기존 카드 그대로
            //  카드는 «회사 1장»이므로 금액도 그 회사 전 연도 «합계»를 쓴다 (2026-09-18)
            const g = coSum.get(String(l.id));
            const t = tdef(l.lead_type);
            const propRefund = !!g && g.ready && g.sum > 0;
            // 인증 만료 DB(벤처·메인비즈·이노비즈) — 만료일이 있으면 「제안서 열기」 = 기존 만료 안내 DM 양식 그대로 (사용자 지시 2026-09-19)
            const cd = t.cert ? dday(expiryOf(l)) : null;
            const propCert = t.cert && !!l.proposal_ready && cd != null;
            // 신규법인 — 벤처확인 기한 안이면 「제안서 열기」 = 벤처확인 · 소득공제 안내 DM (2026-10-17)
            const nd = l.lead_type === 'venture_new' ? dday(newDeadlineOf(l)) : null;
            const propNew = l.lead_type === 'venture_new' && !!l.proposal_ready && nd != null && nd >= 0;
            const prop = propRefund || propCert || propNew;
            const open = propRefund ? () => setPropFor(l) : (propCert || propNew) ? () => openDm(l) : undefined;
            return (
            <div key={l.id} className={'lp4-card' + (prop ? ' prop' : '')} onClick={open} role={prop ? 'button' : undefined} aria-label={prop ? l.corp_name + ' 제안서 열기' : undefined}>
              <div className="lp4-cardTop"><div className="lp4-corp">{l.corp_name}</div><StatusBadge l={l} /></div>
              <div className="lp4-date">수락일 : {fmtKo(l.accepted_at || l.assigned_at)}</div>
              <TypeLine l={l} co={allOf(l)} />
              {propRefund && g && (
                <div className="lp4-refund">
                  <div>
                    <div className="lb">환급 예상{g.years.length ? ' · ' + g.years.join('·') + '년' : ''}{g.count > 1 ? ' 합계' : ''}</div>
                    <div className="amt">{g.sum.toLocaleString()}만원</div>
                    {g.count > 1 && <div className="lp4-multi">같은 대표님 투자 <b>{g.count}건</b> — 제안서는 전부 합해 나옵니다</div>}
                  </div>
                  <span className="go">제안서 열기 ›</span>
                </div>
              )}
              {propCert && cd != null && (
                <div className={'lp4-refund cert' + (cd < 0 ? ' exp' : cd <= 30 ? ' soon' : '')}>
                  <div>
                    <div className="lb">{t.label} 인증 만료 · {fmtDot(expiryOf(l))}</div>
                    <div className="amt">{cd < 0 ? `만료 ${-cd}일 경과` : cd === 0 ? '오늘 만료' : `만료까지 D-${cd}`}</div>
                    <div className="lp4-multi">{cd < 0 ? <>재확인하면 <b>3년 연장</b> — 만료 상태에선 보증·금융 우대가 중단됩니다</> : <>만료 전 재확인 신청 — <b>3년 연장</b>, 혜택 공백 없이 이어집니다</>}</div>
                  </div>
                  <span className="go">제안서 열기 ›</span>
                </div>
              )}
              {propNew && nd != null && (
                <div className={'lp4-refund cert' + (nd <= 60 ? ' soon' : '')}>
                  <div>
                    <div className="lb">벤처확인 기한 · {fmtDot(newDeadlineOf(l))}</div>
                    <div className="amt">{nd === 0 ? '오늘까지' : `기한까지 D-${nd}`}</div>
                    <div className="lp4-multi">벤처확인을 받으면 설립 출자금이 <b>소득공제</b> 대상 — 대표님께 안내해 주세요</div>
                  </div>
                  <span className="go">제안서 열기 ›</span>
                </div>
              )}
              <div className="lp4-hr" />
              <div className="lp4-guide">{guideText(l)}</div>
              <div className="lp4-btns" onClick={(e) => e.stopPropagation()}>
                <button className="lp4-btn" onClick={() => setInfo(l)}>법인정보 확인</button>
                <button className="lp4-btn coral" onClick={() => openMeet(l)}><svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><rect x="5" y="4" width="14" height="17" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /><rect x="9" y="2.5" width="6" height="3" rx="1" fill="currentColor" /><path d="M8.5 13l2.3 2.3L15.5 10.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>나의 미팅현황</button>
              </div>
            </div>);
          })}
          {loaded && shown.length === 0 && (
            <div className="lp4-none">{qq.length >= 2 ? '검색 결과가 없습니다.' : catF !== '전체' ? `${catF} 법인 DB가 없습니다.` : acceptCount > 0 ? '관리 중인 법인 DB가 없습니다.\n수락하기 탭에서 DB를 받아보세요.' : '관리 중인 법인 DB가 없습니다.\n지도사가 배정하면 수락하기 탭에 표시됩니다.'}</div>
          )}
        </>)}
        {tab === 'accept' && (<>
          {/* 정원 배너는 실제로 가득 차 더 수락할 수 없을 때만 (평상시 미노출 — 같은 수치가 탭·목록 헤더에 있음) */}
          {cart && acceptFull && (
            <div className="lp4-cartBar full">정원 가득 참 <b>{cart.size}건</b> <span className="mut">(관리 DB {cart.used}건)</span> — 진행 중인 건을 완료(계약·관리거절)하면 더 받을 수 있어요.</div>
          )}
          {cart && (
            <div className="lp4-listHead">
              <div className="l">배정된 DB <b>{pendingShown.length + poolShown.length}건</b></div>
              <div className="r">관리 DB {cart.used}/{cart.size} · 잔여 {acceptLeft}</div>
            </div>
          )}
          {!hintOff && pendingShown.length > 0 && (
            <div className="lp4-hint">수락하면 관리 DB로 이동해 법인정보·미팅현황을 관리할 수 있습니다.<button className="x" onClick={closeHint} aria-label="안내 닫기">✕</button></div>
          )}
          {pendingShown.map((l) => (
            <div key={l.id} className="lp4-card pend">
              <div className="lp4-cardTop"><div className="lp4-corp">{l.corp_name}</div><span className="lp4-badge pend">수락 대기</span></div>
              <div className="lp4-meta"><span>배정 {fmtDotYo(l.assigned_at)}</span></div>
              <TypeLine l={l} co={allOf(l)} />
              <div className="lp4-acts">
                <button className="lp4-accept" disabled={busy || acceptFull} onClick={() => accept(l)}>{acceptFull ? '정원 가득 참' : '수락하기'}</button>
                <button className="lp4-decline" disabled={busy} onClick={() => setDeclineFor(l)}>받지 않기</button>
              </div>
            </div>
          ))}
          {pendingShown.length > 0 && poolShown.length > 0 && <div className="lp4-secTitle" style={{ marginTop: 14 }}>지점에 공유된 DB {poolShown.length}건</div>}
          {poolShown.map((l) => (
            <div key={l.id} className="lp4-card pend">
              <div className="lp4-cardTop"><div className="lp4-corp">{l.corp_name}</div><span className="lp4-badge ghost">새 DB</span></div>
              <div className="lp4-meta"><span>{l.address || '주소 미등록'}</span><i className="vr" /><span>지점 공유</span></div>
              <TypeLine l={l} co={allOf(l)} />
              <div className="lp4-acts">
                <button className="lp4-accept" disabled={busy || full} onClick={() => claim(l)}>{full ? `정원 가득 참 (${cart!.committed ?? cart!.used}/${cart!.size}건)` : '수락하기'}</button>
              </div>
            </div>
          ))}
          {loaded && poolShown.length === 0 && pendingShown.length === 0 && <div className="lp4-none">{qq.length >= 2 ? '검색 결과가 없습니다.' : '수락할 법인 DB가 없습니다.\n지도사가 배정하면 여기에 먼저 표시됩니다.'}</div>}
        </>)}
        {!loaded && <div className="lp4-none">불러오는 중…</div>}
      </div>
      {info && <LeadPortalInfoSheet lead={info} token={token} onClose={() => setInfo(null)} onToast={say} />}
      {regFor && <MeetRegModal lead={regFor} token={token} onClose={() => setRegFor(null)} onSaved={() => { setRegFor(null); reload(); }} onToast={say} />}
      {declineFor && (
        <div className="lp4-ov" onClick={() => !busy && setDeclineFor(null)}>
          <div className="lp4-sheet bottom lp4-confirm" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="받지 않기 확인">
            <div className="lp4-sheetHead"><b>받지 않기</b><button className="lp4-x" onClick={() => setDeclineFor(null)} aria-label="닫기">×</button></div>
            <div className="lp4-sheetBody">
              <div className="lp4-corp" style={{ fontSize: 16 }}>{declineFor.corp_name}</div>
              <p className="msg">이 DB를 받지 않으면 다시 배정되지 않습니다.</p>
              <p className="sub">지도사가 다른 담당자께 배정할 수 있으며, 되돌릴 수 없습니다.</p>
            </div>
            <div className="lp4-sheetFoot" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 8 }}>
              <button className="lp4-btn" style={{ height: 52 }} disabled={busy} onClick={() => setDeclineFor(null)}>취소</button>
              <button className="lp4-cta warn" disabled={busy} onClick={() => decline(declineFor)}>{busy ? '처리 중…' : '받지 않기'}</button>
            </div>
          </div>
        </div>
      )}
      {toast && <div className="lp4-toast">{toast}</div>}
    </div>
  );
}

/* 미팅 결과 등록 모달 — 관리거절 = 회수·재배정 */
function MeetRegModal({ lead, token, onClose, onSaved, onToast }: { lead: PortalLead; token: string; onClose: () => void; onSaved: (released: boolean) => void; onToast: (m: string) => void }) {
  const [content, setContent] = useState('');
  const [manage, setManage] = useState('관리함');
  const [type, setType] = useState(MEET_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (manage === '관리거절' && !window.confirm('관리거절 시 이 DB는 회수되어 다른 담당자께 재배정됩니다. 계속할까요?')) return;
    setBusy(true);
    try { const r = await portalMeeting(token, lead.id, { content: content.trim(), manage, type }); onSaved(r.released); }
    catch (e) { onToast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="lp4-ov" onClick={onClose}>
      <div className="lp4-sheet bottom" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="미팅 결과 등록">
        <div className="lp4-sheetHead"><b>미팅 결과 등록</b><button className="lp4-x" onClick={onClose} aria-label="닫기">×</button></div>
        <div className="lp4-sheetBody">
          <div className="lp4-corp" style={{ fontSize: 16, marginBottom: 10 }}>{lead.corp_name}</div>
          <textarea className="lp4-ta" rows={4} placeholder="미팅 내용을 입력해주세요." value={content} onChange={(e) => setContent(e.target.value)} />
          <div className="lp4-lab">관리 여부 <i>*</i></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {['관리함', '관리거절'].map((m) => (
              <button key={m} className={'lp4-btn' + (manage === m ? ' sel' : '')} onClick={() => setManage(m)}>{m}</button>
            ))}
          </div>
          <div className="lp4-note">ⓘ 관리거절 선택 시 이 DB는 회수되어 다른 담당자께 재배정됩니다.<br />ⓘ 등록한 결과는 차수(n차)로 누적 저장됩니다.</div>
          <div className="lp4-lab">상담 유형 <i>*</i></div>
          <select className="lp4-sel" value={type} onChange={(e) => setType(e.target.value)}>{MEET_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        </div>
        <div className="lp4-sheetFoot" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 8 }}>
          <button className="lp4-btn" style={{ height: 52 }} onClick={onClose}>취소</button>
          <button className="lp4-cta" disabled={busy} onClick={save}>{busy ? '저장 중…' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}
